import GlobalVariables from "./globalVariables";

const INLINE_DOCUMENT_LIMIT = 5;
const INLINE_DOCUMENT_CHAR_LIMIT = 12000;
const INLINE_DOCUMENT_NAME_LIMIT = 120;

// import EventSource from "eventsource";
class Assistant {
  static SESSION_STORAGE_KEY = "addy-sidebar-agent-session";
  
  /**
   * Get loan-scoped session storage key
   * Ensures one chat per loan by using a loan-specific localStorage key
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @param {string} loanId - Loan ID
   * @returns {string} Loan-scoped storage key
   */
  static getLoanScopedSessionKey(organizationId, userId, loanId) {
    if (!loanId || !organizationId || !userId) {
      return Assistant.SESSION_STORAGE_KEY;
    }
    return `addy-sidebar-agent-session-loan-${organizationId}-${userId}-${loanId}`;
  }
  
  static getEndpoints() {
    return {
      infer: "/businessInference/infer",
      chatStrean: "/api/thread/chat-stream", // Legacy: For email compose/reply
      agentQuery: "/api/agent/query-stream", // New: SSE endpoint for Ask Addy Q&A
      qaStream: "/embeddingsInference/infer/qaStream",
      chatName: "/api/thread/get-chat-name",
    };
  }

  static async *sendMessage(
    appID,
    publicId,
    uid,
    hostname,
    chatId,
    message,
    selectedText,
    name,
    clientHostname,
    selectedChatHistory
  ) {
    const response = await Assistant.getAPIResponse(
      message,
      selectedText,
      publicId,
      hostname,
      chatId,
      appID,
      uid,
      name,
      clientHostname,
      selectedChatHistory
    );
    for await (const chunk of response) {
      yield chunk;
    }
    return response;
  }

  // Used for email replies
  static async *getEmailReplyChat(
    organizationId,
    appID,
    publicId,
    uid,
    hostname,
    chatId,
    message,
    selectedText,
    name,
    clientHostname,
    selectedChatHistory,
    requestParams,
    email,
    promptId,
    computedEmbeddings,
    customInstructions,
    isOldSendMessage = false,
    chatHistoryFromFrontend, // This is an array of the messages so far in this chat
    voiceMode = false, // Add voiceMode parameter with default value false
    abortSignal
  ) {
    // console.log("Request params", requestParams);
    const response = await Assistant.getReplyChatAPIResponse(
      organizationId || null,
      message,
      selectedText,
      publicId,
      hostname,
      chatId,
      appID,
      uid,
      name,
      clientHostname,
      selectedChatHistory,
      requestParams,
      email,
      promptId,
      computedEmbeddings,
      customInstructions,
      isOldSendMessage,
      chatHistoryFromFrontend, // This is an array of the messages so far in this chat
      voiceMode, // Pass voiceMode parameter
      abortSignal
    );
    for await (const chunk of response) {
      yield chunk;
    }
    return response;
  }

  /**
   * NEW: Get agent chat response using the new LangGraph agent system
   * Used for Ask Addy Q&A (not email composition)
   * @param {Object} params - Request parameters
   * @param {string} params.loanId - Optional current loan context for loan-specific queries
   * @returns {AsyncGenerator} Yields response chunks
   */
  static async *getAgentChatResponse({
    query,
    displayQuery,
    isRetry,
    threadId,
    organizationId,
    userId,
    loanId,
    appID,
    kbID,
    mode,
    email,
    inlineDocuments,
    compareMode,
    pricingContext,
    token,
    abortSignal,
    chatWithDocs = false,
  }) {
    // IMPORTANT: Use SSE-specific URL - Firebase emulator doesn't support SSE/streaming
    const ENDPOINT =
      GlobalVariables.getSSEAPIURL() + Assistant.getEndpoints().agentQuery;
    
    console.log("[AssistantModel] 🟢 Generator started executing, endpoint:", ENDPOINT);

    try {
      // For loan-scoped chats, backend will get/create session key from Firestore
      // This ensures one chat per loan across all browsers/devices
      // Don't use localStorage for loan-scoped chats - backend handles persistence
      let storedSessionKey;
      if (!loanId) {
        // Only use localStorage for non-loan chats
        try {
          storedSessionKey = window.localStorage.getItem(
            Assistant.SESSION_STORAGE_KEY
          );
        } catch (_) {
          storedSessionKey = undefined;
        }
      }

      // For loan-scoped chats, don't pass sessionKey - let backend retrieve/create from Firestore
      // For non-loan chats, use threadId or stored session key
      const effectiveSessionKey = loanId
        ? undefined // Backend will handle this
        : threadId || storedSessionKey || undefined;

      const sanitizedInlineDocuments = Assistant.sanitizeInlineDocuments(
        inlineDocuments,
        chatWithDocs ? { documentLimit: Infinity } : undefined
      );

      const payload = {
        query: query || "",
        threadId: loanId ? undefined : (threadId || undefined), // Don't pass threadId for loan-scoped chats
        organizationId: organizationId || "",
        userId: userId || "",
        loanId: loanId || "",
        appID: appID || "",
        kbID: kbID || "",
        sessionKey: effectiveSessionKey, // Backend will get/create loan-scoped key from Firestore if loanId provided
        inlineDocuments: sanitizedInlineDocuments,
        compareMode: !!compareMode,
        metadata: {
          mode: mode || "",
          email: email || "",
          displayQuery: displayQuery || query || "",
          isRetry: !!isRetry,
          compareMode: !!compareMode,
          pricingContext: !!pricingContext,
          // Only sent for Chat with your docs, so other requests are unchanged.
          ...(chatWithDocs ? { chatWithDocs: true } : {}),
        },
      };

      console.log("[AssistantModel] 🔵 New agent request:", {
        endpoint: ENDPOINT,
        hasQuery: !!query,
        threadId,
        organizationId,
        loanId,
        // Debug: trace assistant context being sent to backend
        appID: appID || "(empty - will use default)",
        kbID: kbID || "(empty)",
      });

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          "X-Agent-Stream": "true",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: abortSignal,
      };

      console.log("[AssistantModel] 🟡 About to fetch...");
      const response = await fetch(ENDPOINT, requestOptions);
      console.log("[AssistantModel] 🟢 Fetch completed, status:", response.status, "ok:", response.ok);

      if (!response.ok) {
        console.error(
          "[AssistantModel] ❌ Agent request failed:",
          response.status
        );

        const errorBody = await response.text();
        let parsedError;
        try {
          parsedError = JSON.parse(errorBody);
        } catch (_) {
          parsedError = null;
        }

        const isRateLimit =
          response.status === 429 ||
          parsedError?.error?.type === "rate_limit_error";
        const retryAfterHeader = response.headers.get("Retry-After");
        const retryAfterSeconds = retryAfterHeader
          ? parseInt(retryAfterHeader, 10)
          : undefined;

        yield {
          type: "error",
          errorType: isRateLimit ? "RATE_LIMIT" : "HTTP_ERROR",
          message:
            (isRateLimit
              ? "I'm temporarily handling too many requests. Please wait a moment before trying again."
              : parsedError?.message ||
                parsedError?.error?.message ||
                parsedError?.content) ||
            (isRateLimit
              ? "I'm temporarily handling too many requests."
              : "Sorry, something went wrong. Please try again later."),
          retryAfterSeconds: isRateLimit
            ? retryAfterSeconds || parsedError?.retryAfterSeconds || 60
            : undefined,
          status: response.status,
        };
        return;
      }

      // Determine storage key - only use localStorage for non-loan chats
      // Loan-scoped chats are persisted in backend Firestore
      const storageKey = loanId
        ? null // Don't use localStorage for loan-scoped chats
        : Assistant.SESSION_STORAGE_KEY;

      console.log("[AssistantModel] 🟢 Getting reader from response body...");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      console.log("[AssistantModel] 🟢 Starting to read stream...");

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log("[AssistantModel] 🔴 Stream ended (done=true)");
          break;
        }
        console.log("[AssistantModel] 🟢 Received chunk, length:", value?.length);

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim() || line.startsWith(":")) continue;

          // Parse SSE format: "event: eventType\ndata: {...}"
          if (line.startsWith("data: ")) {
            const dataStr = line.slice(6); // Remove "data: " prefix

            try {
              const data = JSON.parse(dataStr);

              const eventType = data.type;

              const sessionKeyUpdate =
                data.sessionKey ||
                data.metadata?.sessionKey ||
                data.threadId ||
                data.metadata?.threadId;
              if (sessionKeyUpdate) {
                window.chatId = sessionKeyUpdate;
                // Only store in localStorage for non-loan chats
                // Loan-scoped chats are persisted in backend Firestore
                if (storageKey) {
                  try {
                    window.localStorage.setItem(storageKey, sessionKeyUpdate);
                  } catch (_) {
                    // ignore storage errors
                  }
                }
              }

              if (!eventType) {
                // Status / heartbeat events without explicit type
                continue;
              }

              switch (eventType) {
                case "chunk":
                  // Stream individual chunks of the response
                  if (data.content) {
                    yield data.content;
                  }
                  break;

                case "result":
                case "response":
                  // Final complete response - yield the content as a string
                  if (data.content) {
                    // Update thread ID if provided
                    console.log(
                      "[AssistantModel] ✅ Complete response received, yielding content"
                    );
                    // Yield the complete response content as a string so Chat.js can render it
                    yield data.content;
                  }
                  // Also check if result contains clarification_request
                  if (data.type === "clarification_request") {
                    console.log(
                      "[AssistantModel] ❓ Clarification in result event"
                    );
                    yield {
                      type: "clarification",
                      question:
                        data.question ||
                        data.content ||
                        "The agent needs more information.",
                      options: data.options || data.metadata?.options || [],
                      threadId: data.threadId || data.metadata?.threadId,
                    };
                  }
                  break;

                case "connected":
                  // Initial handshake - surface as progress for loader text
                  yield {
                    type: "progress",
                    message: "Thinking...",
                    metadata: data,
                  };
                  break;

                case "status":
                case "progress":
                  // Status / progress updates include friendly message/state
                  yield {
                    type: "progress",
                    message:
                      data.message || data.state || "Processing your query...",
                    metadata: data,
                  };
                  break;

                case "final_response":
                  // The server's final text is authoritative: addylink refs
                  // are resolved and document names are enforced as links
                  // (including correcting wrong targets the model emitted).
                  // Yield it as a typed event so Chat.js REPLACES the
                  // accumulated streamed text instead of appending it.
                  if (data.content) {
                    yield { type: "final_text", content: data.content };
                  }
                  break;

                case "citations":
                  // Inline source citations for the [N] markers in the final
                  // answer (Chat with your docs, DEV-6130).
                  if (Array.isArray(data.citations) && data.citations.length > 0) {
                    yield { type: "citations", citations: data.citations };
                  }
                  break;

                case "links":
                  // Map of short link references (addylink:N) -> real URLs.
                  // Sent as soon as tool results produce links so Chat.js can
                  // resolve references in streamed chunks without waiting.
                  if (data.links && typeof data.links === "object") {
                    yield { type: "links", links: data.links };
                  }
                  break;

                case "pricing_results":
                  if (data.pricingUi) {
                    yield {
                      type: "pricing_results",
                      pricingUi: data.pricingUi,
                    };
                  }
                  break;

                case "complete":
                  // Completion event doesn't need a UI update (result already sent)
                  break;

                case "clarification_request":
                  yield {
                    type: "clarification",
                    question:
                      data.question ||
                      data.content ||
                      "The agent needs more information.",
                    options: data.options || data.metadata?.options || [],
                    sessionKey: sessionKeyUpdate,
                  };
                  break;

                case "trace":
                  // Trace information - yield as progress indicator
                  console.log(
                    "[AssistantModel] 🔍 Trace:",
                    data.metadata?.state
                  );
                  if (data.content) {
                    yield {
                      type: "progress",
                      message: data.content,
                      metadata: data.metadata,
                    };
                  }
                  break;

                case "error": {
                  const rawErrorType =
                    data.error?.type ||
                    data.errorType ||
                    data.metadata?.errorType ||
                    data.metadata?.error?.type;
                  const normalizedErrorType = (() => {
                    if (!rawErrorType) return "AGENT_ERROR";
                    const upper = String(rawErrorType).toUpperCase();
                    if (upper.includes("RATE_LIMIT")) return "RATE_LIMIT";
                    if (upper.includes("NETWORK")) return "NETWORK";
                    if (upper.includes("HTTP")) return "HTTP_ERROR";
                    if (upper.includes("AGENT")) return "AGENT_ERROR";
                    return upper;
                  })();

                  const retryAfterSeconds =
                    data.retryAfterSeconds ||
                    data.metadata?.retryAfterSeconds ||
                    data.error?.retry_after ||
                    data.error?.retryAfterSeconds;

                  const defaultRateLimitMessage =
                    "I'm temporarily handling too many requests. Please wait a moment before trying again.";

                  const message =
                    (normalizedErrorType === "RATE_LIMIT"
                      ? null
                      : data.content ||
                        data.message ||
                        data.error?.message) ||
                    (normalizedErrorType === "RATE_LIMIT"
                      ? defaultRateLimitMessage
                      : "The agent ran into an issue while completing that step.");

                  console.error(
                    "[AssistantModel] ❌ Agent error:",
                    message,
                    { errorType: normalizedErrorType }
                  );

                  yield {
                    type: "error",
                    errorType: normalizedErrorType,
                    message:
                      normalizedErrorType === "RATE_LIMIT"
                        ? defaultRateLimitMessage
                        : message,
                    retryAfterSeconds:
                      retryAfterSeconds &&
                      Number.isFinite(Number(retryAfterSeconds))
                        ? Number(retryAfterSeconds)
                        : undefined,
                  };
                  break;
                }

                case "metrics":
                  console.log(
                    "[AssistantModel] 📊 Metrics:",
                    data.data || data
                  );
                  break;

                default:
                  console.warn(
                    "[AssistantModel] Unknown event type:",
                    eventType,
                    data
                  );
              }
            } catch (parseError) {
              console.error(
                "[AssistantModel] Failed to parse data:",
                dataStr,
                parseError
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("[AssistantModel] ❌ Generator error caught:", error?.name, error?.message, error);
      if (error.name === "AbortError") {
        console.log("[AssistantModel] Fetch aborted");
      } else {
        console.error("[AssistantModel] ❌ Fetch error:", error);
        yield {
          type: "error",
          errorType: "NETWORK",
          message:
            "I couldn't reach the agent service. Please check your connection and try again.",
        };
      }
    }
  }

  /**
   * Resume an in-progress agent session after a clarification prompt.
   * This hits the non-streaming /api/agent/resume endpoint and yields the
   * resulting content (or additional clarification) back to the chat UI.
   * @param {string} params.loanId - Optional current loan context for loan-specific queries
   */
  static async *resumeAgentClarification({
    clarificationResponse,
    sessionKey,
    organizationId,
    userId,
    loanId,
    appID,
    kbID,
    mode,
    email,
    token,
    abortSignal,
  }) {
    // IMPORTANT: Use SSE-specific URL - Firebase emulator doesn't support SSE/streaming
    const ENDPOINT = GlobalVariables.getSSEAPIURL() + "/api/agent/resume";

    try {
      const payload = {
        sessionKey,
        clarificationResponse,
        organizationId: organizationId || "",
        userId: userId || "",
        loanId: loanId || "",
        appID: appID || "",
        kbID: kbID || "",
        metadata: { mode: mode || "", email: email || "" },
      };

      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: abortSignal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[AssistantModel] ❌ Resume request failed:", errorText);
        yield "Sorry, I couldn't continue that task. Please try again.";
        return;
      }

      const data = await response.json();

      if (data.type === "clarification_request") {
        yield {
          type: "clarification",
          question:
            data.question ||
            data.content ||
            "The agent still needs more information.",
          options: data.options || data.metadata?.options || [],
          sessionKey: data.metadata?.sessionKey || sessionKey,
        };
        return;
      }

      if (data.type === "response" && data.pricingUi) {
        yield {
          type: "pricing_results",
          pricingUi: data.pricingUi,
        };
      }

      if (data.type === "response" && data.content) {
        yield data.content;
        return;
      }

      if (data.type === "error") {
        yield data.content || "Sorry, I couldn't continue that task.";
        return;
      }

      // Fallback: stringify whatever came back
      if (data.content) {
        yield data.content;
      } else {
        yield "The agent finished, but I couldn't parse the response.";
      }
    } catch (error) {
      console.error("[AssistantModel] ❌ Resume fetch error:", error);
      yield "Oops... I had a glitch while continuing that task. Please try again.";
    }
  }

  static async getAPIResponse_(
    message,
    selectedText,
    publicId,
    hostName,
    chatId,
    appId,
    uid,
    name,
    clientHostname,
    selectedChatHistory
  ) {
    const ENDPOINT =
      GlobalVariables.getAPIURL() + Assistant.getEndpoints().infer;

    // eslint-disable-next-line max-len
    return await fetch(
      `${ENDPOINT}/qa?user_query=${message}&selectedText=${selectedText}&publicId=${publicId}&host=${hostName}&chatId=${chatId}&appID=${appId}&uid=${uid}&name=${name}&clientHostname=${clientHostname}`
    )
      .then((response) => response.json())
      .then((data) => {
        // console.log(data);
        if (data.response) {
          return data.response;
        } else {
          return "Sorry, I could not understand your query";
        }
      })
      .catch((error) => {
        console.error(error);
        return "Oops... I had a glitch. Please try again later";
      });
  }

  /**
   * @desc Get the chat name
   * @param {String} firstMessage
   * @return {String} chatName
   */
  static async getChatName(firstMessage) {
    const ENDPOINT =
      GlobalVariables.getAPIURL() + Assistant.getEndpoints().chatName;

    return await fetch(`${ENDPOINT}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ firstMessage: firstMessage }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) {
          throw new Error("Error fetching chat name");
        }
        return data.chatName;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  }

  static async *getReplyChatAPIResponse(
    organizationId,
    message,
    selectedText,
    publicId,
    hostName,
    chatId,
    appId,
    uid,
    name,
    clientHostname,
    selectedChatHistory,
    requestParams,
    email,
    promptId,
    computedEmbeddings,
    customInstructions,
    isOldSendMessage = false,
    chatHistoryFromFrontend, // This is an array of the messages so far in this chat
    voiceMode = false, // Add voiceMode parameter with default value false
    abortSignal
  ) {
    // IMPORTANT: Use SSE-specific URL - Firebase emulator doesn't support SSE/streaming
    const ENDPOINT =
      GlobalVariables.getSSEAPIURL() + Assistant.getEndpoints().chatStrean;
    try {
      const payload = {
        organizationId: organizationId,
        requestParams: requestParams,
        uid: uid,
        email: email,
        chatId: chatId,
        promptId: promptId,
        computedEmbeddings: computedEmbeddings,
        subscription: "unlimited",
        name: name,
        customInstructions: customInstructions,
        isClient: true,
        appID: appId,
        host: hostName,
        clientHostname: clientHostname,
        publicId: publicId,
        selectedText: selectedText,
        isOldSendMessage: isOldSendMessage,
        selectedChatHistory: selectedChatHistory,
        chatHistoryFromFrontend: chatHistoryFromFrontend,
        voiceMode: voiceMode, // Add voiceMode flag to payload
      };

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: abortSignal,
      };

      const response = await fetch(ENDPOINT, requestOptions);
      const reader = response.body.getReader();

      if (response.status !== 200) {
        yield "Sorry, something went wrong. Please try again later.";
        return;
      }

      let backupDocumentsFetchedChunk = [];
      let backupDocumentsComparedChunk = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        const chunkString = new TextDecoder().decode(value);

        // This part is used to fix the issue where the documents-fetched response is split into multiple chunks
        if (
          backupDocumentsFetchedChunk.length &&
          this.isJsonString(chunkString)
        ) {
          // console.log("complete & parsing");
          // Valid json
          const backupDocumentsFetched =
            this.joinAndParseBackupDocumentsFetched(
              backupDocumentsFetchedChunk
            );
          if (backupDocumentsFetched) {
            yield backupDocumentsFetched;
          }
          backupDocumentsFetchedChunk = [];
        } else if (
          !this.isJsonString(chunkString) &&
          chunkString.includes("documents-fetched")
        ) {
          // console.log("incomplete start building");
          // first part of documents-fetched response in a case where it's incomplete
          // keep building the chunk until we get a single chunk that's a valid JSON string by itself
          backupDocumentsFetchedChunk.push(chunkString);
        } else if (backupDocumentsFetchedChunk.length) {
          // console.log("incomplete keep building");
          backupDocumentsFetchedChunk.push(chunkString);
        }

        if (
          backupDocumentsComparedChunk.length &&
          this.isJsonString(chunkString)
        ) {
          // console.log("complete & parsing");
          // Valid json
          const backupDocumentsCompared =
            this.joinAndParseBackupDocumentsCompared(
              backupDocumentsComparedChunk
            );
          if (backupDocumentsCompared) {
            yield backupDocumentsCompared;
          }
          backupDocumentsComparedChunk = [];
        } else if (
          !this.isJsonString(chunkString) &&
          chunkString.includes("documents-compared")
        ) {
          // console.log("incomplete start building");
          // first part of documents-fetched response in a case where it's incomplete
          // keep building the chunk until we get a single chunk that's a valid JSON string by itself
          backupDocumentsComparedChunk.push(chunkString);
        } else if (backupDocumentsComparedChunk.length) {
          // console.log("incomplete keep building");
          backupDocumentsComparedChunk.push(chunkString);
        }

        // extract message from proper key based on
        // whether the data is in the expected format or not.

        // eslint-disable-next-line no-prototype-builtins
        // console.log("Chunk string", chunkString);
        if (this.isJsonString(chunkString)) {
          const parsedData = JSON.parse(chunkString);
          let messages = null;
          if (parsedData && parsedData.success) {
            if (parsedData.response === "documents-fetched") {
              messages =
                "documents-fetched-" + JSON.stringify(parsedData.documents);
            } else if (parsedData.response === "documents-compared") {
              messages =
                "documents-compared-" + JSON.stringify(parsedData.documents);
            } else {
              if (!parsedData.finished) {
                // Get the
                const decoder = new TextDecoder();
                messages =
                  typeof parsedData.response === "string"
                    ? parsedData.response
                    : decoder
                        .decode(new Uint8Array(parsedData.response.data))
                        .split("data:")[1];
                messages = messages.replace(/"/g, "").replace(/#/g, "");
                // console.log("Messages", messages);
                // const text = Buffer.from(parsedData.response.data);
                // eslint-disable-next-line max-len
                // messages = text.toString().split("data:")[1].trim().replace(/"/g, "");
              } else {
                // It is the final message
                if (parsedData.chatId) {
                  // Update the current window's chatId
                  window.chatId = parsedData.chatId;
                }
                let finalMessage = parsedData.response.replaceAll(
                  "event: message",
                  ""
                );
                finalMessage = finalMessage.replaceAll("data: ", "");
                finalMessage = finalMessage.split('"').join("");
                messages = this.cleanEmailString(finalMessage);

                // console.log("Final message", messages);
              }
            }

            // console.log('__________messages', messages)
          }
          yield messages || "";
        } else {
          // Check if it's a JSON array instead
          let chunkStringformat = chunkString.replace(/}\s*{/g, "},{");
          chunkStringformat = "[" + chunkStringformat + "]";
          const jsonArray = this.getJSONArray(chunkStringformat);
          if (jsonArray && jsonArray.length > 0) {
            for (let i = 0; i < jsonArray.length; i++) {
              const item = jsonArray[i];
              let messages = null;
              if (item && item.success) {
                // Get the
                if (!item.finished) {
                  const decoder = new TextDecoder();
                  messages =
                    typeof item.response === "string"
                      ? item.response
                      : decoder
                          .decode(new Uint8Array(item.response.data))
                          .split("data:")[1];
                  messages = messages.replace(/"/g, "").replace(/#/g, "");
                  // console.log("Messages", messages);
                } else {
                  // It is the final message
                  if (item.chatId) {
                    // Update the current window's chatId
                    window.chatId = item.chatId;
                  }
                  let finalMessage = item.response.replaceAll(
                    "event: message",
                    ""
                  );
                  finalMessage = finalMessage.replaceAll("data: ", "");
                  finalMessage = finalMessage.split('"').join("");
                  messages = this.cleanEmailString(finalMessage);

                  // console.log("Final message", messages);
                }
              }
              yield messages || "";
            }
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        // Fetch was aborted
        console.log("Fetch aborted");
      } else {
        console.error("Fetch error:", error);
        yield "Oops... I had a glitch. Please try again later";
      }
    }
  }

  static async *getAPIResponse(
    message,
    selectedText,
    publicId,
    hostName,
    chatId,
    appId,
    uid,
    name,
    clientHostname,
    selectedChatHistory
  ) {
    // IMPORTANT: Use SSE-specific URL - Firebase emulator doesn't support SSE/streaming
    const ENDPOINT =
      GlobalVariables.getSSEAPIURL() + Assistant.getEndpoints().qaStream;

    // console.log("Selected chat history", selectedChatHistory);
    try {
      const payload = {
        user_query: message,
        selectedText: selectedText,
        publicId: publicId,
        host: hostName,
        chatId: chatId,
        appID: appId,
        uid: uid,
        name: name,
        clientHostname: clientHostname,
        selectedChatHistory: selectedChatHistory,
      };

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      };

      const response = await fetch(ENDPOINT, requestOptions);
      const reader = response.body.getReader();

      if (response.status !== 200) {
        yield "Sorry, something went wrong. Please try again later.";
        return;
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        const chunkString = new TextDecoder().decode(value);
        yield chunkString;
      }
    } catch (error) {
      console.error(error);
      yield "Oops... I had a glitch. Please try again later";
    }
  }

  /**
   * @param {Array} documents
   * @param {Object} [options]
   * @param {number} [options.documentLimit] - Chat with your docs raises this
   *   so every picked loan document is sent (DEV-6130).
   */
  static sanitizeInlineDocuments(
    documents,
    { documentLimit = INLINE_DOCUMENT_LIMIT } = {}
  ) {
    if (!Array.isArray(documents) || documents.length === 0) {
      return undefined;
    }

    const sanitized = [];

    for (const doc of documents) {
      if (!doc || typeof doc !== "object") {
        continue;
      }

      const rawText =
        typeof doc.textContent === "string" ? doc.textContent.trim() : "";
      if (!rawText) {
        continue;
      }

      const safeName =
        typeof doc.name === "string" && doc.name.trim().length
          ? doc.name.trim().slice(0, INLINE_DOCUMENT_NAME_LIMIT)
          : `Attachment ${sanitized.length + 1}`;

      // Loan documents carry their file URL so citations can open them;
      // uploads have none and are sent exactly as before.
      const safeUrl = /^https?:\/\//i.test(doc.url || "") ? doc.url.trim() : "";
      sanitized.push({
        name: safeName,
        textContent: rawText.slice(0, INLINE_DOCUMENT_CHAR_LIMIT),
        ...(safeUrl ? { url: safeUrl } : {}),
      });

      if (sanitized.length >= documentLimit) {
        break;
      }
    }

    return sanitized.length ? sanitized : undefined;
  }

  static cleanEmailString(emailString) {
    // emailString = emailString.replace(/\n/g, "");
    // emailString = emailString.replace(/\\n\\n/g, "\n\n");
    emailString = emailString.replace(/\\n/g, "\n");
    emailString = emailString.replace(/\\t/g, "\t");
    // replace all # with empty string
    emailString = emailString.replace(/#/g, "");
    emailString = emailString.trim(); // Remove leading "\n\n" characters
    return {
      subject: "",
      emailString: emailString,
    };
  }

  static isJsonString(str) {
    try {
      JSON.parse(str);
    } catch (e) {
      //   console.error("Error parsing JSON string", str, e);
      return false;
    }
    return true;
  }

  static getJSONArray(str) {
    try {
      const parsedData = JSON.parse(str);
      // return the actual array
      return parsedData;
    } catch (e) {
      //   console.error("Error parsing JSON array", str, e);
      return false;
    }
  }

  static joinAndParseBackupDocumentsFetched(backupDocumentsFetchedChunk) {
    const fullBackupChunk = backupDocumentsFetchedChunk.join("");
    let documentsFetched = "";

    if (this.isJsonString(fullBackupChunk)) {
      const { success, response, documents } = JSON.parse(fullBackupChunk);

      if (success && response === "documents-fetched") {
        documentsFetched = `documents-fetched-${JSON.stringify(documents)}`;
      }
    }
    return documentsFetched;
  }

  static joinAndParseBackupDocumentsCompared(backupDocumentsComparedChunk) {
    const fullBackupChunk = backupDocumentsComparedChunk.join("");
    let documentsCompared = "";

    if (this.isJsonString(fullBackupChunk)) {
      const { success, response, documents } = JSON.parse(fullBackupChunk);

      if (success && response === "documents-compared") {
        documentsCompared = `documents-compared-${JSON.stringify(documents)}`;
      }
    }
    return documentsCompared;
  }
}

// module.exports = Assistant;

export default Assistant;
